/**
 * Shared resolver for Google place_id -> {lat, lng}.
 *
 * The frontend NEVER reads cached lat/lng from a backend-supplied geocode
 * payload (Google ToS §B.5.3 caps user-displayed lat/lng caching at 30
 * days; place_id has no such cap per §A.3). Instead, every map component
 * passes the stored place_id to Google's Place class and lets Maps return
 * fresh coordinates at render time.
 *
 * This module exists so multiple map renderers (LocationMapComponents'
 * MerchantLocationMap / TripRouteMap, GeoMapPanel, future renderers)
 * share a single implementation AND a single session-lifetime cache —
 * iterating a map (drop a pin, redraw with a different gradient) does
 * not re-bill the same place_id resolution to Google.
 *
 * Cache: module-scoped Map<place_id, {lat,lng}|null>. Cleared on full
 * reload. No TTL — within a session, a place_id either resolves once
 * (kept) or fails once (kept as null so we don't keep retrying). Across
 * sessions, no persistence — that would re-introduce the §B.5.3 30-day
 * cap on cached coords.
 *
 * Returns null for: empty/missing place_id, an AWS-PlaceId-shape value
 * (the brief AWS Location experiment left some records that won't
 * resolve via Google's Place class), or a Place.fetchFields rejection
 * (expired/invalid place_id). Callers render no marker for null pins
 * and (in GeoMapPanel) show a "N pin(s) could not be located" footer
 * chip rather than silently dropping them — never let a forensic
 * omission be invisible.
 */

const COORD_CACHE = new Map();

/**
 * Resolve a Google place_id to fresh {lat, lng} coordinates via the Maps
 * JS Place class. The Maps API libraries `places` must already be loaded
 * (use the useGoogleMapsApi() hook to gate readiness).
 *
 * Returns a Promise<{lat, lng} | null>. Cached for the lifetime of the
 * browser session.
 */
export async function resolvePlaceIdToCoords(placeId) {
  if (!placeId) return null;
  if (COORD_CACHE.has(placeId)) return COORD_CACHE.get(placeId);
  try {
    const place = new window.google.maps.places.Place({ id: placeId });
    await place.fetchFields({ fields: ['location'] });
    const loc = place.location;
    if (!loc) {
      COORD_CACHE.set(placeId, null);
      return null;
    }
    const coords = { lat: loc.lat(), lng: loc.lng() };
    COORD_CACHE.set(placeId, coords);
    return coords;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Place.fetchFields failed for place_id', placeId, err);
    COORD_CACHE.set(placeId, null);
    return null;
  }
}

/**
 * Resolve N place_ids in parallel. Preserves input order; returns null
 * entries for unresolved place_ids so the caller can render a "could
 * not be located" indicator.
 */
export async function resolvePlaceIdsToCoords(placeIds) {
  if (!placeIds || placeIds.length === 0) return [];
  return Promise.all(placeIds.map((pid) => resolvePlaceIdToCoords(pid)));
}

/** Test-only — empty the cache. */
export function __resetPlaceIdCacheForTests() {
  COORD_CACHE.clear();
}
